import { FC, ReactElement, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Head from 'next/head'
import { Parallax } from 'react-parallax'
import CountUp from 'react-countup'
import { useDispatch } from 'react-redux'
import { useInView } from 'react-intersection-observer'
import parse from 'html-react-parser'
import { trackEvent } from 'utils/eventTracking'
import { showSimulation } from 'store/simulation/actions/showSimulation'
import { VIDEO_ICELAND_1_URL_NL, VIDEO_ICELAND_1_URL_FR, VIDEO_ICELAND_2_URL, MOBILE_BREAK_POINT } from './constants'
import styles from './ProjectDetail.module.scss'
import buttonStyles from 'components/button/Button.module.scss'
import { PROJECTS } from 'components/projects/constants'
import classNames from 'classnames'
import Image from 'next/image'
import ProjectSlider from 'components/projects/ProjectSlider'
import Button from 'components/button/Button'
import heroImage from '../../../../public/images/features/projects/iceland/header.jpg'
import { TITLE_POSTFIX } from 'constants/seo'
import { getCloudinarySEOImage } from 'utils/helpers'
import { useRouter } from 'next/router'
import { Language } from 'types/language'

const ProjectIceland: FC = (): ReactElement => {
  // NEXT ROUTER
  const { locale } = useRouter()

  // I18N
  const { t } = useTranslation(['projectIceland', 'projects'])

  // STATE
  const [variableImageParralaxTwo, setVariableImageParralaxTwo] = useState<string>('/images/features/projects/iceland/parallax-2.jpg')

  // REDUX STORE
  const dispatch = useDispatch()

  // REACT-INTERSECTION-OBSERVER
  const [inViewRef, inView] = useInView({
    threshold: 0,
    triggerOnce: true
  })

  /**
   * Handles the click event on the CTA button
   */
  const handleCtaClick = () => {
    dispatch(showSimulation())

    // Track 'Simulation Started' event @Semetis
    trackEvent('simulation_started')
  }

  useEffect(() => {
    if (window.innerWidth < MOBILE_BREAK_POINT) {
      setVariableImageParralaxTwo('/images/features/projects/iceland/parallax-2-mobile.jpg')
    }
  }, [])

  // Filter out current project
  const otherProjects = PROJECTS.filter((project) => project.id !== 'iceland')

  return (
    <>
      {/* SEO */}
      <Head>
        <title>
          {t('projects:projectTitleIceland')} {TITLE_POSTFIX}
        </title>
        <meta name="description" content={t('projects:projectDescriptionIceland')} />
        <meta property="og:title" content={`${t('projects:projectTitleIceland')} ${TITLE_POSTFIX}`} key="title" />
        <meta property="og:description" content={t('projects:projectDescriptionIceland')} />
        <meta
          property="og:image"
          content={getCloudinarySEOImage('https://www.boltenergie.be/images/components/projects/iceland.jpg')}
          key="image"
        />
      </Head>

      <div className={classNames(styles['project-detail'], styles['project-iceland'])}>
        {/* PAGE CONTENT */}
        <section className={classNames('section', styles.hero)}>
          <div className="container has-bg-image">
            <h1 className={styles.title}>{parse(t('title', 'Duizend certificaten<br /><span>terug naar IJsland</span>'))}</h1>
            <div className={styles.preface}>
              {t(
                'titlePreface',
                'In de zomer van 2020 gaf Bolt-afgevaardigde Koen 1000 groene stroomcertificaten terug aan de eerste minister van IJsland, zodat ze niet meer  gebruikt konden worden om Belgische contracten groen te kleuren.'
              )}
            </div>
          </div>

          <Image
            priority
            src={heroImage}
            layout="fill"
            objectFit="cover"
            objectPosition="center 20%"
            placeholder="blur"
            alt="Bolt Energie"
          />
        </section>

        <section className={classNames('section', 'is-dark', styles.description)}>
          <div className="container">
            <div className={styles['description-text']}>
              {t(
                'description',
                '9 op 10 groene energiecontracten in Vlaanderen zijn niet groen. 9 op 10! Je koopt dan grijze stroom met een groene sticker, sjoemelstroom. Dit willen we halt toe roepen met de #stopsjoemelstroom-campagne.'
              )}
            </div>
          </div>
        </section>

        <section className={classNames('section', styles.about)}>
          <div className="container">
            <div className={classNames('columns', styles.columns)}>
              <div className="column is-half">
                <Image
                  src="/images/features/projects/iceland/about-1.jpg"
                  width={1200}
                  height={800}
                  alt={t('aboutTopTitle', 'Sjoemelstroom?')}
                />
              </div>
              <div className="column is-half">
                <h2>{t('aboutTopTitle', 'Sjoemelstroom?')}</h2>
                <p>
                  {t(
                    'aboutTopDescription',
                    'Van alle energie die in Vlaanderen wordt geproduceerd, is slechts 10% groen. Toch denken zo’n 45% van de Vlamingen dat ze groene energie kopen. Hoe kan dat? Iedereen die groene energie opwekt, krijgt hiervoor een certificaat. Deze certificaten worden nationaal én internationaal verhandeld, zonder dat je de energie er fysiek bijkoopt. De certificaten kleuren grijze stroom in België dus ‘groen’, zonder dat er ook effectief groene stroom wordt opgewekt in ons land. Deze grijze stroom, met een groene sticker, noemen we sjoemelstroom. Om de duurzame energieontwikkeling in ons land te stimuleren, moeten we bewust kiezen voor lokaal opgewekte stroom. Streekstroom, geen sjoemelstroom.'
                  )}
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className={classNames('section', 'is-dark', styles.video)}>
          <div className="container">
            <div className={styles.content}>
              <h2>{parse(t('videoTopTitle', 'Deze video werd massaal gedeeld'))}</h2>
              <p>
                {t(
                  'videoTopDescription',
                  'Om de problematiek rond de certificaten of ‘garanties van oorsprong’ eenvoudig uit te leggen, maakten we een video. We vroegen deze te delen met vrienden en familie, zodat de boodschap meer mensen kon bereiken. In ruil voor 1000 shares beloofden we 1000 certificaten aan te kopen en terug te brengen naar IJsland, zodat ze niet meer gebruikt konden worden om Belgische contracten groen te kleuren.'
                )}
              </p>
              <div className={styles.video}>
                <div className="video-16-9">
                  <iframe
                    title="Stop sjoemelstroom"
                    src={locale === Language.DUTCH ? VIDEO_ICELAND_1_URL_NL : VIDEO_ICELAND_1_URL_FR}
                    frameBorder="0"
                    allowFullScreen
                  />
                </div>
              </div>
              <p>
                {parse(
                  t(
                    'videoCaption',
                    'In hun rubriek ‘Factcheck’ markeerde de De Standaard ons campagnefilmpje als ‘Waar’. Ze onderzochten of het wel degelijk klopt dat 9 op 10 groene energiecontracten in Vlaanderen de transitie naar groen niet vooruit helpt. <a href="#" target="_blank">Lees het artikel hier</a>.'
                  )
                )}
              </p>
            </div>
          </div>
        </section>

        <section className={classNames('section', styles.quote)}>
          <Parallax bgImage="/images/features/projects/iceland/parallax-1.jpg" strength={200}>
            <div className={classNames('container', styles.container)}>
              <div className={styles['quote-container']}>
                <div className={styles['quote-text']}>
                  {parse(t('quote', '&quot;90 procent van de Vlaamse groene energie helpt de transitie niet vooruit.&quot;'))}
                </div>
                <div className={styles['quote-source']}>{t('quoteSource', 'De Standaard')}</div>
              </div>
            </div>
          </Parallax>
        </section>

        <section className={classNames('section', styles.about)}>
          <div className="container">
            <div className={classNames('columns', styles.columns)}>
              <div className="column is-half">
                <h2>{t('aboutBottomTitle', 'Missie geslaagd')}</h2>
                <p>
                  {t(
                    'aboutBottomDescription',
                    'Bolt stuurde de IJslandse Belg Koen Kjarten met de ferry én een Tesla-auto naar IJsland, samen met 1000 groene stroomcertificaten of garanties van oorsprong. Op 7 augustus 2020 was het zo ver. 1000 IJslandse certificaten werden door Koen teruggeven aan de IJslandse premier voor haar kantoor in Reykjavik, met de dringende vraag te stoppen met het verhandelen ervan. Haar reactie: “Dit probleem ga ik aankaarten bij onze minister van energie”, aldus premier Katrín Jakobsdóttir.'
                  )}
                </p>
              </div>
              <div className="column is-half">
                <Image
                  src="/images/features/projects/iceland/about-2.jpg"
                  width={1200}
                  height={800}
                  alt={t('aboutBottomTitle', 'Missie geslaagd')}
                />
              </div>
            </div>
          </div>
        </section>

        <section className={classNames('section', 'is-dark', styles.video)}>
          <div className="container">
            <div className={styles.content}>
              <h2>{parse(t('videoBottomTitle', 'Het <span>verhaal van Koen</span>'))}</h2>
              <p>
                {t(
                  'videoBottomDescription',
                  'En Koen? Die mocht zijn verhaal doen op radio, tv en in de kranten. Bolt went viral, toch voor even. Koen blikt samen met Bolt terug op zijn missie naar #stopsjoemelstroom.'
                )}
              </p>
              <div className={styles.video}>
                <div className="video-16-9">
                  <iframe title="Koen in IJsland" src={VIDEO_ICELAND_2_URL} frameBorder="0" allowFullScreen />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className={classNames('section', styles.about)}>
          <div className="container">
            <div className={classNames('columns', styles.columns)}>
              <div className="column is-half">
                <Image
                  src="/images/features/projects/iceland/about-3.jpg"
                  width={1200}
                  height={800}
                  alt={t('aboutBottomTwoTitle', 'En nu?')}
                />
              </div>
              <div className="column is-half">
                <h2>{t('aboutBottomTwoTitle', 'En nu?')}</h2>
                <p>
                  {t(
                    'aboutBottomTwoDescription',
                    'Het #stopsjoemelstroom-verhaal stopt niet hier. Voorlopig blijven energieleveranciers voor geen geld \'groene\' stroom verkopen zonder zelf te investeren in duurzame energie en vragen daar onterecht nog eens extra hoge prijzen voor. Pieterjan Verhaeghen, CEO van Bolt: "Energiegebruikers worden dubbel bedot. De IJslanders weten dat ze de facto groene stroom hebben, maar beseffen niet dat die dat op papier helemaal niet meer is, doordat de garanties van oorsprong verkocht worden aan landen als België. Belgen dénken dat ze de facto groene stroom hebben, maar beseffen niet dat het grijze stroom is die dankzij IJslandse papieren groen gewassen is." De wereld op zijn kop, right?'
                  )}
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className={classNames('section', 'is-dark', styles['call-out'])}>
          <div className="container">
            <div className={classNames('columns', styles.columns)}>
              <div className="column is-half">
                <h2>{parse(t('callOutTitle', '<span>Oproep</span> aan beleidsmakers'))}</h2>
                <p>
                  {parse(
                    t(
                      'callOutDescription',
                      'Naast de awareness-campagnes rond #stopsjoemelstroom, richt Bolt zich ook tot de Belgische beleidsmakers. Vandaag kan je via de herkomstvergelijker van de VREG (Vlaamse Regulator van de Elektriciteits- en Gasmarkt) uitzoeken wat de herkomst van jouw energie is. "Dat is een mooi en zinvol initiatief, maar helaas geen stap die je moét doorlopen als je een energieleverancier kiest", vertelt Pieterjan. Hij pleit samen met het volledige Bolt-team, voor een \'nutri-score voor energie\', die meteen \'op de verpakking\' toont hoe groen het contract is. "We hebben in ons land niets aan geïmporteerde buitenlandse garanties van oorsprong. Wat echt van tel is zijn reële en nabije investeringen in productie van wind- en zonne-energie, zodat we degelijke streekstroom hebben, in plaats van groen gelabelde grijze energie."'
                    )
                  )}
                </p>
              </div>
              <div className={classNames('column', styles.column, 'is-half', styles['right-aligned'])}>
                <div className={styles['card']}>
                  <div className={styles['card-image']} />
                  <div className={styles['card-content']}>
                    <h3>{t('callOutCardTitle', 'Belgisch energieplatform Bolt veroorzaakt ophef in Reykjavik')}</h3>
                    <p>
                      {t(
                        'callOutCardDescription',
                        'Hun uitdrukkelijk verzoek: "Stop met het verhandelen van oorspronggaranties van groene stroom, anders raken we in België nooit bijgebeend met echt groene energie."'
                      )}
                    </p>
                    <a
                      href="https://bolt.prezly.com/belgisch-energieplatform-bolt-veroorzaakt-ophef-in-reykjavik-bij-teruggave-van-1000-garanties-van-oorsprong-aan-ijslandse-premier"
                      target="_blank"
                      rel="noreferrer"
                      className={classNames(
                        buttonStyles.btn,
                        buttonStyles['btn-primary'],
                        buttonStyles['with-gradient'],
                        buttonStyles['is-fullwidth-mobile']
                      )}
                    >
                      {t('callOutCardButton', 'Lees meer')}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className={classNames('section', styles.quote)}>
          <Parallax bgImage={variableImageParralaxTwo} strength={200}>
            <div className={classNames('container', styles.container, styles['right-aligned'])}>
              <div className={styles['quote-container']}>
                <div className={styles['quote-text']}>
                  {parse(
                    t('quoteBottom', '&quot;Het is te gemakkelijk voor leveranciers om hun grijze imago op te poetsen naar groen&quot;')
                  )}
                </div>
                <div className={styles['quote-source']}>{t('quoteBottomSource', 'Pieterjan Verhaeghen, co-founder Bolt')}</div>
              </div>
            </div>
          </Parallax>
        </section>

        <section className={classNames('section', styles.numbers)} ref={inViewRef}>
          <div className="container">
            <h2>{t('numbersTitle', 'Opvallende cijfers')}</h2>
            <div className={classNames('columns', styles.columns)}>
              <div className={classNames('column', styles.column)}>
                <CountUp start={inView ? 0 : 1000} end={1000} delay={0.5}>
                  {({ countUpRef }) => (
                    <div className={styles.number}>
                      <span ref={countUpRef} />
                    </div>
                  )}
                </CountUp>
                <div className={styles.label}>{t('numberOneLabel', 'kWh kan je groen maken met 1 certificaat')}</div>
              </div>
              <div className={classNames('column', styles.column)}>
                <CountUp start={inView ? 0 : 0.23} end={0.23} delay={1} decimals={2} decimal=",">
                  {({ countUpRef }) => (
                    <div className={styles.number}>
                      <span ref={countUpRef} />
                    </div>
                  )}
                </CountUp>
                <div className={styles.label}>{t('numberTwoLabel', 'waarde 1 certificaat (in EUR)')}</div>
              </div>
              <div className={classNames('column', styles.column)}>
                <CountUp start={inView ? 0 : 3} end={3} delay={1.5}>
                  {({ countUpRef }) => (
                    <div className={styles.number}>
                      <span ref={countUpRef} />
                    </div>
                  )}
                </CountUp>
                <div className={styles.label}>
                  {t('numberThreeLabel', "certificaten heb je nodig om je huishouden 1 jaar lang van 'groene' stroom te voorzien")}
                </div>
              </div>
            </div>
            <div className={classNames(styles.result, styles.small)}>
              {parse(
                t(
                  'numbersResult',
                  "Energieleveranciers kunnen dus voor geen geld 'groene' stroom verkopen zonder zelf te investeren in duurzame energie en vragen daar <span>onterecht nog eens extra hoge prijzen voor</span>."
                )
              )}
            </div>
          </div>
        </section>

        <section className={classNames('section', styles.partners)}>
          <div className="container">
            <h2>{t('partnersTitle', 'Partners')}</h2>
            <p>
              {t(
                'partnersDescription',
                'Door COVID-19 werd het onmogelijk om met de zeilboot naar IJsland te varen. Om toch zo duurzaam mogelijk de reis af te leggen, kon Koen rekenen op een Tesla via Tesla Owners Club Belgium. Bedankt!'
              )}
            </p>
            <div className={classNames('columns', styles.columns, 'is-vertically-aligned')}>
              <div className={classNames('column', styles.column, styles['text-left'])}>
                <a href="https://www.teslaclub.be" target="_blank" rel="noopener noreferrer">
                  <Image
                    src="/images/features/projects/iceland/partners/tesla-owners-belgium.png"
                    alt="Tesla Owners Belgium"
                    quality={100}
                    height={136}
                    width={136}
                  />
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className={classNames('section', 'is-dark', styles.projects)}>
          <div className="container">
            <h2>{t('projectsTitle', 'Bekijk ook onze andere projecten')}</h2>
            <ProjectSlider projects={otherProjects} />
          </div>
        </section>

        <section className={classNames('section', styles.cta)}>
          <div className="container">
            <div className={styles.content}>
              <h2>{parse(t('ctaTitle', '<span>Zelf overstappen?</span><br />Bereken jouw prijs!'))}</h2>
              <p>{t('ctaDescription', 'In minder dan 5 minuten kan je je registreren. Wij regelen alles!')}</p>
              <Button onClick={() => handleCtaClick()} isFullWidthMobile withGradient>
                {t('ctaButton', 'Bereken jouw prijs')}
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  )
}

export default ProjectIceland
